import {chain} from 'lodash';
import React, {FC, ReactElement, ReactNode} from 'react';
import styled from 'styled-components';

import {ExtractProps, isElementOfType} from '../../../../helpers/src/react/reactHelpers';
import {Button} from './button';
import {useButtonGroupSameWidthChildren} from './buttonGroup/buttonGroupContext';

/*
 * Props.
 */

type ButtonProps = ExtractProps<typeof Button>;

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: inline-grid;
`;

const StyledContentDiv = styled.div`
  grid-row: 1;
  grid-column: 1;
  text-align: center;
`;

const StyledOtherContentDiv = styled(StyledContentDiv)`
  opacity: 0;
  pointer-events: none;
`;

/*
 * Component.
 */

/** Button that will have the same size as its peers in a Button Group. */
export const SameWidthButton: FC<ButtonProps> = (props) => {
  const sameWidthChildren = useButtonGroupSameWidthChildren();
  const {children} = props;

  return (
    <Button {...props}>
      <StyledWrapperDiv>
        <StyledContentDiv>{children}</StyledContentDiv>
        {renderOtherContent(children, sameWidthChildren)}
      </StyledWrapperDiv>
    </Button>
  );
};

// Using index as key because position should be stable.
/* eslint react/no-array-index-key: 0 */
function renderOtherContent(children: ReactNode, sameWidthChildren: ReadonlyArray<ReactNode>) {
  return chain(sameWidthChildren)
    .filter((c) => c !== children)
    .map((c, index) => (
      <StyledOtherContentDiv key={index} aria-hidden>
        {c}
      </StyledOtherContentDiv>
    ))
    .value();
}

/*
 * Type guard.
 */

export function isSameWidthButton(src: any): src is ReactElement<ButtonProps> {
  return isElementOfType(src, SameWidthButton);
}
